import { useEffect, useCallback, useRef, useState } from 'react';

const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutos en milisegundos
const WARNING_BEFORE_LOGOUT = 60 * 1000; // Mostrar aviso 1 minuto antes

const useInactivityLogout = (onLogout) => {
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const logout = useCallback(() => {
    setShowWarning(false);
    if (onLogout) {
      onLogout();
    }
  }, [onLogout]);

  const showLogoutWarning = useCallback(() => {
    setShowWarning(true);
    setSecondsRemaining(60);
    
    // Countdown
    const countdownInterval = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Guardar referencia para limpiar
    warningTimeoutRef.current = countdownInterval;
  }, []);

  const resetTimer = useCallback(() => {
    // Limpiar timers existentes
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearInterval(warningTimeoutRef.current);
    }
    
    setShowWarning(false);
    setSecondsRemaining(60);

    // Configurar timer para mostrar aviso (14 minutos)
    timeoutRef.current = setTimeout(() => {
      showLogoutWarning();
      
      // Configurar timer para logout (1 minuto después del aviso)
      timeoutRef.current = setTimeout(() => {
        logout();
      }, WARNING_BEFORE_LOGOUT);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT);
  }, [logout, showLogoutWarning]);

  const continueSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Eventos que indican actividad del usuario
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle para no reiniciar el timer con cada movimiento mínimo
    let lastActivity = Date.now();
    const throttleMs = 1000; // Solo procesar actividad cada segundo

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > throttleMs) {
        lastActivity = now;
        if (!showWarning) {
          resetTimer();
        }
      }
    };

    // Agregar listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Iniciar timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearInterval(warningTimeoutRef.current);
      }
    };
  }, [resetTimer, showWarning]);

  return {
    showWarning,
    secondsRemaining,
    continueSession,
    logout
  };
};

export default useInactivityLogout;
